import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { FaCamera } from "react-icons/fa";
import { FiPhoneCall } from "react-icons/fi";
import { IoMdCheckboxOutline } from "react-icons/io";
import {
  IoCloseSharp,
  IoLocationOutline,
  IoMailOutline,
} from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { server } from "../App";
import { AddressAutocomplete } from "../component/AddressAutocomplete";
import { useUpdateProfileMutation } from "../store/api/userSlice";
import { logInUser } from "../store/reducer/UserReducer";
import { multiPartData } from "../utils";
import { capitalizeFirstLetter } from "../utils/dateAndTimeConversion";
import "./Profile.scss";

const initialState = {
  full_name: "",
  company_name: "",
  bio: "",
  profile_image: "",
  country: "",
  state: "",
  city: "",
  street: "",
  zip_code: "",
  phone: "",
  email: "",
};

const Profile = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const isParent = user?.role === "parent";
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visiblebio, setVisiblebio] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [profileImage, setProfileImage] = useState("");
  const [errors, setErrors] = useState({});

  // update profile query
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const handleShow2 = () => setShow2(true);
  const handleShow = () => {
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setVisible(false);
  };

  const handleClose2 = () => {
    setShow2(false);
    setVisiblebio(false);
  };

  const handleClose3 = () => {
    setShow3(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name !== "company_name") {
      setErrors((prev) => ({
        ...prev,
        [name]: value
          ? ""
          : `${capitalizeFirstLetter(name.replace("_", " "))} is required`,
      }));
    }
  };

  // Change Address
  const handleChangeAddress = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Address Autocomplete Select
  const handleAddressSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      street: address.street,
      city: address.city,
      state: address.state,
      country: address.country,
      zip_code: address.zip_code,
      latitude: address.latitude,
      longitude: address.longitude,
    }));

    // Clear address-related errors
    setErrors((prev) => ({
      ...prev,
      street: "",
      city: "",
      state: "",
      country: "",
      zip_code: "",
    }));
  };

  const fileHandler = (e) => {
    const file = e.target.files[0];

    if (file) {
      if (
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png"
      ) {
        // Preview the image
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImage(reader.result);
          previewsFile(file);
        };
        reader.readAsDataURL(file);
      } else {
        toast.error(t("image_format_error"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }
  };

  const previewsFile = (file) => {
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        setFormData((prev) => ({
          ...prev,
          profile_image: file,
        }));
        setProfileImage(reader.result);
        handleSubmit("image", file);
      },
      false,
    );
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (
        key === "profile_image" ||
        key === "bio" ||
        key === "latitude" ||
        key === "longitude" ||
        key === "country" ||
        key === "state" ||
        key === "city" ||
        key === "zip_code" ||
        key === "company_name"
      )
        return; // Skip optional fields

      if (typeof value === "object" && value !== null) {
        // Handle nested address object
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (!subValue || subValue?.trim() === "") {
            newErrors[subKey] = `${capitalizeFirstLetter(
              subKey.replace("_", " "),
            )} is required`;
          }
        });
      } else {
        if (!value || value.trim() === "") {
          newErrors[key] = `${capitalizeFirstLetter(
            key.replace("_", " "),
          )} is required`;
        }
      }
    });
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (key, file = null) => {
    if (!validateForm() && !file) return;
    if (key === "bio" && !formData.bio.trim()) {
      setErrors((prev) => ({ ...prev, bio: t("bio_required") }));
      return;
    }
    toast.dismiss();

    const data = file
      ? multiPartData({ ...formData, profile_image: file })
      : multiPartData(formData);

    try {
      const response = await updateProfile({
        data,
        token: user.token,
        id: user.id,
      }).unwrap();
      if (response.statusCode === 200) {
        dispatch(
          logInUser({ ...response.data, token: user.token, id: user.id }),
        );
        if (key === "image") {
          setShow3(true);
        }
        if (key === "profile") {
          setVisible(true);
        }
        if (key === "bio") {
          setVisiblebio(true);
        }
      }
    } catch (err) {
      const mesg = err.data?.message || t("something_went_wrong");
      toast.error(mesg);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name,
        company_name: user.company_name,
        profile_image: user.profile_image,
        bio: user.bio,
        city: user.city,
        country: user.country?.name || "",
        state: user.state?.name || "",
        street: user.street,
        zip_code: user.zip_code,
        latitude: user.latitude || "",
        longitude: user.longitude || "",
        phone: user.phone,
        email: user.email,
      });
      setProfileImage(`${server}${user.profile_image}`);
    }
  }, [user]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-start justify-content-between">
        <div className="whtBox p-3 profileLeft">
          <div className="profileImg schoolImg">
            <img
              src={profileImage || "/images/defaultProfile.png"}
              alt="Profile"
            />{" "}
            <span className="uploadPhoto">
              <FaCamera />
              <input
                type="file"
                className="file"
                onChange={fileHandler}
                accept=".png, .jpg, .jpeg"
                onClick={(e) => (e.target.value = null)}
              />
            </span>
          </div>

          <div className="dbSubHd pt-3 pb-3">{`${user?.full_name}`}</div>
          <ul className="m-0 p-0 userInfo">
            <li className="mb-2 d-flex align-items-center">
              <IoLocationOutline className="icon me-3" />
              {user?.street || ""}
            </li>
            <li className="mb-2 d-flex align-items-center">
              <FiPhoneCall className="icon me-3" /> {user?.phone}
            </li>
            <li className="mb-2 d-flex align-items-center">
              <IoMailOutline className="icon me-3" /> {user?.email}
            </li>
          </ul>
          <div className="pt-3">
            <Button className="smallBtn small" onClick={handleShow}>
              {t("edit_profile")}
            </Button>
          </div>
        </div>
        <div className="whtBox p-3 profileRight">
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <div className="dbSubHd pb-3">{t("bio")}</div>
            <div className="pb-3">
              <Button className="smallBtn small" onClick={handleShow2}>
                {t("edit_bio")}
              </Button>
            </div>
          </div>
          <div className="contentBox">
            <p>{user.bio}</p>
          </div>
        </div>
      </div>

      <Modal show={show}>
        <Button className="closePopup scrollable-modal" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5 scrollable-modal-content">
          <div style={{ display: visible === true ? "none" : "block" }}>
            <h2 className="text-center fpHd mb-3">{t("edit_profile")}</h2>
            <ul className="form p-0 m-0">
              <li>
                <label className="filedHd">{t("errors.full_name")}</label>
                <input
                  type="text"
                  className="formInput"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                />
                {errors.full_name && (
                  <span style={{ color: "red" }}>{errors.full_name}</span>
                )}
              </li>
              {!isParent && (
                <li>
                  <label className="filedHd">{t("errors.company_name")}</label>
                  <input
                    type="text"
                    className="formInput"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                  />
                  {errors.company_name && (
                    <span style={{ color: "red" }}>{errors.company_name}</span>
                  )}
                </li>
              )}

              <li>
                <label className="filedHd">{t("address")}</label>
                <AddressAutocomplete
                  name="street"
                  value={formData.street}
                  onChange={handleChangeAddress}
                  onSelect={handleAddressSelect}
                />
                {(errors.street ||
                  errors.city ||
                  errors.state ||
                  errors.country ||
                  errors.zip_code) && (
                  <span style={{ color: "red" }}>
                    {errors.street ||
                      errors.city ||
                      errors.state ||
                      errors.country ||
                      errors.zip_code}
                  </span>
                )}
              </li>
              <li>
                <label className="filedHd">{t("phone_number")}</label>
                <input
                  type="text"
                  className="formInput"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <span style={{ color: "red" }}>{errors.phone}</span>
                )}
              </li>
              <li>
                <label className="filedHd">{t("email")}</label>
                <input
                  type="text"
                  className="formInput"
                  name="email"
                  value={formData.email}
                  readOnly
                />
              </li>
              <li className="d-flex col-12">
                <div className="col-6 pe-2 pt-2">
                  <Button
                    className="smallBtn full cancel"
                    onClick={handleClose}
                  >
                    {t("btns.cancel")}
                  </Button>
                </div>
                <div className="col-6 ps-2 pt-2">
                  <Button
                    className="smallBtn full"
                    disabled={isLoading}
                    onClick={() => handleSubmit("profile")}
                  >
                    {isLoading ? (
                      <div className="spinner-border" role="status">
                        <span className="sr-only p-0"></span>
                      </div>
                    ) : (
                      t("btns.update")
                    )}
                  </Button>
                </div>
              </li>
            </ul>
          </div>
          <div style={{ display: visible === true ? "block" : "none" }}>
            <div className="text-center mb-3 checkIcon">
              <IoMdCheckboxOutline />
            </div>
            <div className="text-center mb-3 deleteHd">
              {t("profile_updated")}
            </div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose}>
                {t("btns.close")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal show={show2}>
        <Button className="closePopup" onClick={handleClose2}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div style={{ display: visiblebio ? "none" : "block" }}>
            <h2 className="text-center fpHd mb-3">{t("edit_bio")}</h2>
            <ul className="form p-0 m-0">
              <li>
                <textarea
                  rows="8"
                  cols="8"
                  className="formInput"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                ></textarea>
                {errors.bio && (
                  <span style={{ color: "red" }}>{errors.bio}</span>
                )}
              </li>
              <li className="d-flex col-12">
                <div className="col-6 pe-2 pt-2">
                  <Button
                    className="smallBtn full cancel"
                    onClick={handleClose2}
                  >
                    {t("btns.cancel")}
                  </Button>
                </div>
                <div className="col-6 ps-2 pt-2">
                  <Button
                    className="smallBtn full"
                    disabled={isLoading}
                    onClick={() => handleSubmit("bio")}
                  >
                    {isLoading ? (
                      <div className="spinner-border" role="status">
                        <span className="sr-only p-0"></span>
                      </div>
                    ) : (
                      t("btns.update")
                    )}
                  </Button>
                </div>
              </li>
            </ul>
          </div>
          <div style={{ display: visiblebio ? "block" : "none" }}>
            <div className="text-center mb-3 checkIcon">
              <IoMdCheckboxOutline />
            </div>
            <div className="text-center mb-3 deleteHd">{t("bio_updated")}</div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose2}>
                {t("btns.close")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal show={show3}>
        <Button className="closePopup" onClick={handleClose3}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 checkIcon">
              <IoMdCheckboxOutline />
            </div>
            <div className="text-center mb-3 deleteHd">
              {t("profile_image_updated")}
            </div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose3}>
                {t("btns.close")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default Profile;
